// ** Modules
import Image from 'next/image';

// ** Components
import Link from '@/components/common/Link';
import { Icon } from '@/components/common/icons';

// ** UI
import IconButton from '@mui/material/IconButton';
import Box, { type BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';

// ** Utils
import { logoSwitcher } from '@/utils/ui/styleUtils';

// ** Types
import type React from 'react';
import type { AppContext } from '@/types/contextTypes';
import type { LayoutProps } from '@/types/layoutProps';

// ** Props for Component
type VerticalLayout = NonNullable<LayoutProps['verticalLayoutProps']>;
type NavMenu = NonNullable<VerticalLayout['navMenu']>;
type Props = {
	host: AppContext['host'];
	navHover: boolean;
	collapsedNavWidth: number;
	hidden: LayoutProps['hidden'];
	navigationBorderWidth: number;
	toggleNavVisibility: () => void;
	settings: LayoutProps['settings'];
	saveSettings: LayoutProps['saveSettings'];
	navMenuBranding?: NavMenu['branding'];
	menuLockedIcon?: NavMenu['lockedIcon'];
	menuUnlockedIcon?: NavMenu['unlockedIcon'];
	scrollTop?: number; // scroll position from menu container
};

// ** Styled Types
type ExtendedBoxProps = BoxProps & {
	navcollapsed?: string; // optional string to indicate collapsed state
};

// ** Styled Components
const MenuHeaderWrapper = styled(Box)<ExtendedBoxProps>(({ theme, navcollapsed }) => ({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	paddingRight: navcollapsed === 'true' ? 0 : theme.spacing(4.5),
	transition: 'all 0.3s ease-in-out',
	height: navcollapsed === 'true' ? 80 : 130, // shrink header height when collapsed
	position: 'relative', // needed for shadow overlay
	overflow: 'hidden'
}));

const StyledLink = styled(Link)({
	display: 'flex',
	alignItems: 'center',
	textDecoration: 'none',
	transition: 'all 0.25s ease-in-out'
});

// ** Menu Icons
const MenuLockedIcon = ({ userMenuLockedIcon }: { userMenuLockedIcon?: React.ReactNode }) =>
	userMenuLockedIcon || <Icon name="mdi:radiobox-marked" />;

const MenuUnlockedIcon = ({ userMenuUnlockedIcon }: { userMenuUnlockedIcon?: React.ReactNode }) =>
	userMenuUnlockedIcon || <Icon name="mdi:radiobox-blank" />;

// ** VerticalNavHeader Component
const VerticalNavHeader = ({
	host,
	hidden,
	navHover,
	settings,
	scrollTop = 0,
	saveSettings,
	collapsedNavWidth,
	toggleNavVisibility,
	navigationBorderWidth,
	menuLockedIcon: userMenuLockedIcon,
	navMenuBranding: userVerticalNavMenuBranding,
	menuUnlockedIcon: userMenuUnlockedIcon,
	...props
}: Props) => {
	const { navcollapsed } = settings;

	// ** Dynamic left padding for header content
	const menuHeaderPaddingLeft = () => {
		if (navcollapsed && !navHover) {
			if (userVerticalNavMenuBranding) return 0;
			return (collapsedNavWidth - navigationBorderWidth - 30) / 18;
		}
		return 6;
	};

	// ** Determine if toggle button should be visible
	const showToggleButton = !navcollapsed || (navcollapsed && navHover);

	return (
		<MenuHeaderWrapper navcollapsed={(navcollapsed && !navHover)?.toString()} sx={{ pl: menuHeaderPaddingLeft() }}>
			{/* Brand / Logo */}
			{userVerticalNavMenuBranding ? (
				userVerticalNavMenuBranding(props)
			) : (
				<StyledLink href="/" passHref>
					<Box sx={{ mt: 1.5, transition: 'all 0.25s ease-in-out' }}>
						<Image
							src={`${logoSwitcher(host)}`}
							alt="Logo"
							height={(navcollapsed && navHover) || !navcollapsed ? 100 : 65}
							width={(navcollapsed && navHover) || !navcollapsed ? 95 : 50}
						/>
					</Box>
				</StyledLink>
			)}

			{/* Shadow overlay at bottom */}
			<Box
				sx={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					width: '100%',
					height: 10,
					pointerEvents: 'none',
					background: 'linear-gradient(rgba(0,0,0,0.1), transparent)',
					opacity: Math.min(scrollTop / 20, 1),
					transition: 'opacity 0.3s ease-in-out'
				}}
			/>

			{/* Toggle button for collapsing/expanding nav */}
			{!hidden && showToggleButton && (
				<IconButton
					disableRipple
					disableFocusRipple
					onClick={() => saveSettings({ ...settings, navcollapsed: !navcollapsed })}
					sx={{
						p: 0,
						mt: 12,
						color: 'text.primary',
						backgroundColor: 'transparent !important'
					}}
				>
					{navcollapsed ? (
						<MenuUnlockedIcon userMenuUnlockedIcon={userMenuUnlockedIcon} />
					) : (
						<MenuLockedIcon userMenuLockedIcon={userMenuLockedIcon} />
					)}
				</IconButton>
			)}

			{/* Toggle button for hidden nav mode */}
			{hidden && (
				<IconButton disableRipple disableFocusRipple onClick={toggleNavVisibility} sx={{ p: 0, mt: 12 }}>
					<Icon name="mdi-arrow-left-drop-circle-outline" fontSize={20} />
				</IconButton>
			)}
		</MenuHeaderWrapper>
	);
};

export default VerticalNavHeader;
